/**
 * Loads the download-on-demand chart packs manifest on startup and starts the
 * connectivity watch (so "View online" is only offered when connected). Mirrors
 * BsbManifestLoader: a headless bolt-on with no UI. Downloaded packs are
 * re-registered by the store as the manifest loads.
 */
import { useEffect } from 'react';
import { isRescueStart } from '@/diag/safeStart';
import { useChartPacksStore } from '@/store/chartPacks';
import { initNetworkWatch } from '@/store/network';

export function ChartPacksLoader() {
  const loadManifest = useChartPacksStore((s) => s.loadManifest);
  const restoreDownloaded = useChartPacksStore((s) => s.restoreDownloaded);
  useEffect(() => {
    // Saved packs come back FIRST, off the device, before we ask the network
    // anything. Whatever happens to the remote list after this, the charts we
    // already hold are on the map. Both run: the list adds packs we could get,
    // the restore puts back the ones we have.
    // A rescue launch (two starts died young) leaves the downloaded packs
    // alone. The saved view was the first suspect; the packs are the second,
    // and between them they are everything that decides how much work a start
    // does. Standing both down means the app OPENS — which matters beyond the
    // chart, because an app that opens can send a Diagnostics dump, and an app
    // that cannot is a bug nobody can see.
    //
    // Nothing is deleted. The packs are still on the device and come back on
    // the next healthy start, or when the skipper turns them on by hand.
    if (isRescueStart()) {
      console.warn('[packs] rescue start: leaving downloaded packs stood down');
      void loadManifest();
      return;
    }
    void restoreDownloaded().finally(() => void loadManifest());
    let cleanup: (() => void) | undefined;
    void initNetworkWatch().then((c) => {
      cleanup = c;
    });
    return () => cleanup?.();
  }, [loadManifest, restoreDownloaded]);
  return null;
}
